import React, { useEffect, useRef, useState } from 'react';

/**
 * Audio player that lines the recording up with the CALL's talk time.
 *
 * Why (bug #16): the app uploads the phone's built-in call recording. Most
 * OEM recorders start recording when the outgoing call is DIALED (Android's
 * OFFHOOK fires at dial time for outgoing calls), so the audio file also
 * contains the ringing before the customer answered. The stored call /
 * recording duration is the device CallLog talk time (answer → hang-up), so
 * the list said e.g. 0:45 while the player showed 1:05.
 *
 * Both the recording and the talk time END at hang-up, so the talk portion
 * is the LAST `talkDuration` seconds of the file:
 *     ringOffset = audioLength - talkDuration
 * On load the player seeks to that offset (playback starts when the call was
 * answered) and shows the talk duration. "Play from start" plays the full
 * file including the ringing. No server-side trimming — the original file is
 * never modified, so downloads and AI analysis still get the full audio.
 *
 * Offsets under MIN_OFFSET seconds are treated as encoder jitter and ignored.
 */
const MIN_OFFSET = 1.5;

const fmt = (seconds) => {
  const s = Math.max(0, Math.floor(Number(seconds) || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

const TalkTimeAudio = ({ src, talkDuration, className = 'w-full', ...rest }) => {
  const audioRef = useRef(null);
  const [ringOffset, setRingOffset] = useState(0);
  const [audioLength, setAudioLength] = useState(null);

  // New file → recompute the offset
  useEffect(() => {
    setRingOffset(0);
    setAudioLength(null);
  }, [src, talkDuration]);

  const handleLoadedMetadata = () => {
    const audio = audioRef.current;
    if (!audio) return;
    const length = audio.duration;
    // Some containers (e.g. webm/blob) report Infinity/NaN — no reliable offset
    if (!Number.isFinite(length) || length <= 0) return;
    setAudioLength(length);
    const talk = Number(talkDuration) || 0;
    if (talk <= 0) return;
    const offset = length - talk;
    if (offset >= MIN_OFFSET) {
      setRingOffset(offset);
      try {
        audio.currentTime = offset;
      } catch {
        // Seeking not supported by this source — play from 0
      }
    }
  };

  const playFrom = (seconds) => {
    const audio = audioRef.current;
    if (!audio) return;
    try {
      audio.currentTime = seconds;
      audio.play().catch(() => {});
    } catch {
      // ignore
    }
  };

  return (
    <div>
      <audio
        ref={audioRef}
        controls
        className={className}
        src={src}
        preload="metadata"
        onLoadedMetadata={handleLoadedMetadata}
        {...rest}
      >
        Your browser does not support the audio element.
      </audio>
      {ringOffset > 0 && (
        <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500">
          <span>
            Talk time {fmt(talkDuration)} · playback starts when the call was answered
            ({fmt(ringOffset)} of ringing skipped; full file {fmt(audioLength)})
          </span>
          <button
            type="button"
            className="text-primary-600 hover:underline font-medium"
            onClick={() => playFrom(0)}
          >
            Play from start
          </button>
          <button
            type="button"
            className="text-primary-600 hover:underline font-medium"
            onClick={() => playFrom(ringOffset)}
          >
            Jump to answer
          </button>
        </div>
      )}
    </div>
  );
};

export default TalkTimeAudio;
